import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check, Clock, Shield, Star, AlertCircle, Loader2, ArrowRight, FileText, ChevronRight, Plus, X, MessageSquare, Send } from 'lucide-react';
import { authFetch } from '../../utils/authFetch';
import { useAuth } from '../../context/AuthContext';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const MONO = { fontFamily: "'IBM Plex Mono', monospace" };
const SERIF = { fontFamily: "'Bodoni Moda', Georgia, serif" };

// Legal Terms component
const LegalTerms = ({ accepted, onAccept }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-border">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm text-left"
      >
        <span className="font-medium flex items-center gap-2">
          <FileText className="w-4 h-4 text-muted-foreground" />
          Service Agreement & Terms
        </span>
        <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && (
        <div className="border-t border-border px-4 py-4 text-xs text-muted-foreground leading-relaxed space-y-3 max-h-64 overflow-y-auto" style={MONO}>
          <p><strong className="text-foreground">FASHIONSPYDER DEDICATED HIRING SERVICE AGREEMENT</strong></p>
          <p><strong className="text-foreground">1. SERVICE DESCRIPTION</strong><br />
          FashionSpyder ("Platform") provides a dedicated fashion talent search service ("Service"). Upon activation, the Platform assigns a dedicated talent scout to identify and pre-screen qualified candidates for the Client's specified role.</p>
          <p><strong className="text-foreground">2. FEES & PAYMENT STRUCTURE</strong><br />
          a) Monthly Retainer: billed monthly via auto-renewing subscription at the amount shown in your personalised quotation above. Renews automatically each month until cancelled.<br />
          b) Setup Fee (if applicable): a one-time fee shown in your quotation, charged on the first invoice alongside the first month's retainer.<br />
          c) All amounts are in EUR and exclusive of any applicable taxes.</p>
          <p><strong className="text-foreground">3. SERVICE DURATION & TERMINATION</strong><br />
          The Service renews monthly. You may cancel at any time; access and billing continue until the end of the current paid month. No long-term lock-in.</p>
          <p><strong className="text-foreground">4. NON-CIRCUMVENTION</strong><br />
          Client agrees not to hire, directly or indirectly, any candidate presented by FashionSpyder without payment of the agreed success fee, for a period of 12 months from first introduction. Breach of this clause entitles FashionSpyder to full success fee compensation.</p>
          <p><strong className="text-foreground">5. CONFIDENTIALITY</strong><br />
          Both parties agree to maintain strict confidentiality regarding candidate identities, compensation details, and search parameters. Candidate data is processed in accordance with GDPR (Regulation EU 2016/679).</p>
          <p><strong className="text-foreground">6. DATA PROCESSING</strong><br />
          Client acts as Data Controller for candidate data used in the hiring process. FashionSpyder acts as Data Processor. Candidate consent for data sharing is obtained through the Flence platform. A Data Processing Agreement (DPA) is incorporated by reference.</p>
          <p><strong className="text-foreground">7. LIMITATION OF LIABILITY</strong><br />
          FashionSpyder's maximum liability is limited to fees paid in the 3 months preceding the claim. The Platform does not guarantee placement within any specific timeframe.</p>
          <p><strong className="text-foreground">8. GOVERNING LAW & JURISDICTION</strong><br />
          This Agreement is governed by Italian law. Disputes shall be resolved by the Courts of Milan, Italy, unless EU consumer law provides otherwise.</p>
          <p><strong className="text-foreground">9. ACCEPTANCE</strong><br />
          By clicking "I Accept the Terms" below, you confirm that you have read, understood, and agree to be bound by this Agreement, and that you have the authority to enter into this Agreement on behalf of your organisation. This constitutes a legally binding electronic signature.</p>
          <p className="text-foreground/50">Agreement version 1.0 — February 2026 | FashionSpyder is a brand operated by Flence S.r.l.</p>
        </div>
      )}
      <div className="border-t border-border px-4 py-3">
        <label className="flex items-start gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={accepted}
            onChange={e => onAccept(e.target.checked)}
            className="mt-0.5 flex-shrink-0"
            data-testid="legal-accept-checkbox"
          />
          <span className="text-xs text-muted-foreground leading-relaxed">
            I have read and accept the <strong className="text-foreground">Service Agreement & Terms</strong>, including the monthly auto-renewing retainer and any one-time setup fee shown in my quotation above.
          </span>
        </label>
      </div>
    </div>
  );
};

// Request form — multi-role; HR can scout several roles in one engagement
const emptyRole = () => ({ role_title: '', role_level: 'Senior', expected_salary_min: '', expected_salary_max: '' });

const RequestForm = ({ onSubmit, loading }) => {
  const [roles, setRoles] = useState([emptyRole()]);
  const [form, setForm] = useState({ timeline_weeks: 12, description: '', additional_notes: '' });
  const [error, setError] = useState('');

  const LEVELS = ['Junior', 'Mid-level', 'Senior', 'Lead', 'Director', 'VP', 'C-level'];
  const updateRole = (i, key, val) => setRoles(rs => rs.map((r, idx) => idx === i ? { ...r, [key]: val } : r));
  const addRole = () => setRoles(rs => [...rs, emptyRole()]);
  const removeRole = (i) => setRoles(rs => rs.filter((_, idx) => idx !== i));

  const handleSubmit = (e) => {
    e.preventDefault();
    const filled = roles.filter(r => r.role_title.trim());
    if (filled.length === 0) { setError('Add at least one role title'); return; }
    setError('');
    const first = filled[0];
    // Encode all roles into the brief so admin sees the full scope (multi-role = basis of the quotation)
    const rolesBlock = filled.map((r, i) =>
      `${i + 1}. ${r.role_title.trim()} — ${r.role_level}` +
      ((r.expected_salary_min || r.expected_salary_max) ? ` · €${Number(r.expected_salary_min) || '?'}–${Number(r.expected_salary_max) || '?'}` : '')
    ).join('\n');
    const fullDescription =
      `ROLES REQUESTED (${filled.length}):\n${rolesBlock}` +
      (form.description.trim() ? `\n\nBRIEF:\n${form.description.trim()}` : '');
    onSubmit({
      role_title: filled.length > 1 ? `${first.role_title.trim()} (+${filled.length - 1} more)` : first.role_title.trim(),
      role_level: first.role_level,
      expected_salary_min: Number(first.expected_salary_min) || null,
      expected_salary_max: Number(first.expected_salary_max) || null,
      timeline_weeks: form.timeline_weeks,
      description: fullDescription,
      additional_notes: form.additional_notes,
      roles: filled.map(r => ({ title: r.role_title.trim(), level: r.role_level, salary_min: Number(r.expected_salary_min) || null, salary_max: Number(r.expected_salary_max) || null })),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="dedicated-request-form">
      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2">
          <AlertCircle className="w-4 h-4" />{error}
        </div>
      )}

      <div className="space-y-4">
        {roles.map((role, i) => (
          <div key={i} className="border border-border p-4 relative" data-testid={`dedicated-role-${i}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs uppercase tracking-widest text-muted-foreground" style={MONO}>Role {i + 1}</span>
              {roles.length > 1 && (
                <button type="button" onClick={() => removeRole(i)} className="text-muted-foreground hover:text-red-600 transition-colors" data-testid={`remove-role-${i}`}>
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground block mb-1.5" style={MONO}>Role Title *</label>
                <input type="text" value={role.role_title} onChange={e => updateRole(i, 'role_title', e.target.value)}
                  placeholder="e.g. Head of Design" className="w-full border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-foreground" data-testid={`dedicated-role-input-${i}`} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground block mb-1.5" style={MONO}>Seniority Level</label>
                <select value={role.role_level} onChange={e => updateRole(i, 'role_level', e.target.value)}
                  className="w-full border border-border bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-foreground">
                  {LEVELS.map(l => <option key={l}>{l}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-3">
              <label className="text-xs uppercase tracking-widest text-muted-foreground block mb-1.5" style={MONO}>Expected Annual Salary Range (€)</label>
              <div className="grid grid-cols-2 gap-3">
                <input type="number" value={role.expected_salary_min} onChange={e => updateRole(i, 'expected_salary_min', e.target.value)}
                  placeholder="Min (e.g. 60000)" className="w-full border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-foreground" />
                <input type="number" value={role.expected_salary_max} onChange={e => updateRole(i, 'expected_salary_max', e.target.value)}
                  placeholder="Max (e.g. 90000)" className="w-full border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-foreground" />
              </div>
            </div>
          </div>
        ))}
        <button type="button" onClick={addRole}
          className="w-full flex items-center justify-center gap-2 py-2.5 border border-dashed border-border text-sm text-muted-foreground hover:border-foreground hover:text-foreground transition-colors"
          data-testid="add-role-btn">
          <Plus className="w-4 h-4" /> Add another role
        </button>
      </div>

      <div>
        <label className="text-xs uppercase tracking-widest text-muted-foreground block mb-1.5" style={MONO}>Target Timeline (weeks)</label>
        <input type="number" value={form.timeline_weeks} onChange={e => setForm(f => ({ ...f, timeline_weeks: e.target.value }))}
          min={4} max={26} className="w-full border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-foreground" />
      </div>

      <div>
        <label className="text-xs uppercase tracking-widest text-muted-foreground block mb-1.5" style={MONO}>Engagement Brief</label>
        <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          placeholder="Describe the roles, required aesthetic, culture, team context, must-haves..." rows={4}
          className="w-full border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-foreground resize-none" />
      </div>

      <div>
        <label className="text-xs uppercase tracking-widest text-muted-foreground block mb-1.5" style={MONO}>Additional Context (optional)</label>
        <textarea value={form.additional_notes} onChange={e => setForm(f => ({ ...f, additional_notes: e.target.value }))}
          placeholder="Confidentiality needs, preferred cities, travel requirements, etc." rows={2}
          className="w-full border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-foreground resize-none" />
      </div>

      <button type="submit" disabled={loading}
        className="w-full flex items-center justify-center gap-2 py-3.5 bg-foreground text-background text-sm font-medium hover:bg-foreground/90 disabled:opacity-60 transition-colors"
        data-testid="dedicated-submit-btn">
        {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</> : <>Submit Enquiry <ArrowRight className="w-4 h-4" /></>}
      </button>

      <p className="text-xs text-center text-muted-foreground">
        Our team reviews every request personally and sends you a tailored monthly quote — no obligation until you choose to proceed.
      </p>
    </form>
  );
};

// Curated talent recommendations (HR client view)
const CuratedTalent = ({ recommendations, onReact }) => {
  if (!recommendations || recommendations.length === 0) return null;
  return (
    <div className="border border-border bg-white" data-testid="dedicated-curated-talent">
      <p className="text-xs uppercase tracking-widest text-muted-foreground px-4 py-3 border-b border-border flex items-center gap-2" style={MONO}>
        <Star className="w-3.5 h-3.5 text-[hsl(var(--primary))]" /> Curated for you
      </p>
      <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {recommendations.map(rec => {
          const c = rec.candidate || {};
          return (
            <div key={rec.id} className="border border-border p-3 flex flex-col gap-2" data-testid={`curated-card-${rec.id}`}>
              <div className="flex items-center gap-3">
                {c.photo_url ? <img src={c.photo_url} alt="" className="w-12 h-12 object-cover" /> : <div className="w-12 h-12 bg-secondary" />}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{c.full_name || `Candidate #${rec.candidate_id}`}</p>
                  <p className="text-xs text-muted-foreground truncate">{c.core_role || '—'}{c.location_city ? ` · ${c.location_city}` : ''}</p>
                </div>
                {c.overall_ranking != null && (
                  <span className="text-xs font-semibold" style={MONO}>{Number(c.overall_ranking).toFixed(1)}</span>
                )}
              </div>
              {rec.admin_note && (
                <p className="text-xs text-muted-foreground italic border-l-2 border-[hsl(var(--primary))] pl-2">"{rec.admin_note}"</p>
              )}
              <div className="flex items-center gap-2 mt-1">
                <a href={`/candidate/${rec.candidate_id}`} target="_blank" rel="noreferrer"
                  className="text-xs border border-border px-3 py-1.5 hover:bg-secondary flex items-center gap-1" data-testid={`curated-view-${rec.id}`}>
                  View <ArrowRight className="w-3 h-3" />
                </a>
                {rec.client_status === 'interested' ? (
                  <span className="text-xs text-green-700 flex items-center gap-1"><Check className="w-3 h-3" /> Interested</span>
                ) : rec.client_status === 'passed' ? (
                  <span className="text-xs text-muted-foreground">Passed</span>
                ) : (
                  <div className="flex items-center gap-2">
                    <button onClick={() => onReact(rec.id, 'interested')} className="text-xs bg-[hsl(var(--primary))] text-white px-3 py-1.5" data-testid={`curated-interested-${rec.id}`}>Interested</button>
                    <button onClick={() => onReact(rec.id, 'passed')} className="text-xs border border-border px-3 py-1.5 hover:bg-secondary" data-testid={`curated-pass-${rec.id}`}>Pass</button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Ticket-style message thread (HR client view)
const ClientThread = ({ messages, onSend, sending }) => {
  const [text, setText] = useState('');
  const submit = () => { if (text.trim()) { onSend(text.trim()); setText(''); } };
  return (
    <div className="border border-border bg-white" data-testid="dedicated-thread">
      <p className="text-xs uppercase tracking-widest text-muted-foreground px-4 py-3 border-b border-border flex items-center gap-2" style={MONO}>
        <MessageSquare className="w-3.5 h-3.5" /> Messages with your scout
      </p>
      <div className="max-h-72 overflow-y-auto p-4 space-y-2.5">
        {(!messages || messages.length === 0) && (
          <p className="text-xs text-muted-foreground text-center py-4">No messages yet. Your dedicated scout will post updates here — you can reply anytime.</p>
        )}
        {(messages || []).map(m => (
          <div key={m.id} className={`flex ${m.sender_role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] px-3.5 py-2.5 text-sm ${m.sender_role === 'user' ? 'bg-foreground text-background' : 'bg-[hsl(var(--secondary))] text-foreground'}`}>
              <p className="whitespace-pre-line">{m.message}</p>
              <p className={`text-[10px] mt-1.5 ${m.sender_role === 'user' ? 'text-background/50' : 'text-muted-foreground'}`}>
                {m.sender_role === 'user' ? 'You' : 'FashionSpyder'} · {new Date(m.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-2 p-3 border-t border-border">
        <input value={text} onChange={e => setText(e.target.value)} onKeyDown={e => e.key === 'Enter' && submit()}
          placeholder="Write a message..." className="flex-1 text-sm border border-border px-3 py-2.5 focus:outline-none focus:border-foreground"
          data-testid="client-msg-input" />
        <button onClick={submit} disabled={sending} className="bg-foreground text-background px-4 py-2.5 disabled:opacity-50" data-testid="client-msg-send">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
};

// Status display
const StatusPanel = ({ status, subscription, onActivate, activating }) => {
  const [termsAccepted, setTermsAccepted] = useState(false);

  const quoteSent = status?.quote_status === 'sent';
  const isActive = subscription?.status === 'active' || status?.status === 'active';
  const monthly = Number(status?.quote_monthly_amount || subscription?.monthly_amount || 0);
  const setup = Number(status?.quote_setup_fee || subscription?.setup_fee || 0);
  const dueToday = monthly + setup;

  const stages = [
    { key: 'pending',  label: 'Enquiry submitted', done: true },
    { key: 'quoted',   label: 'Tailored quote received', done: quoteSent || isActive },
    { key: 'active',   label: 'Service activated', done: isActive },
  ];

  return (
    <div className="space-y-5">
      {/* Status stages */}
      <div className="border border-border p-5 space-y-3">
        {stages.map((s) => (
          <div key={s.key} className="flex items-center gap-3">
            <div className={`w-5 h-5 flex-shrink-0 flex items-center justify-center border ${s.done ? 'bg-foreground border-foreground' : 'border-border'}`}>
              {s.done && <Check className="w-3 h-3 text-background" />}
            </div>
            <span className={`text-sm ${s.done ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>{s.label}</span>
          </div>
        ))}
      </div>

      {/* Quote received — show custom proposal + activate */}
      {quoteSent && !isActive && (
        <div className="space-y-4" data-testid="dedicated-quote-block">
          <div className="border border-[hsl(var(--primary)/0.4)] bg-[hsl(var(--primary)/0.04)] p-5">
            <p className="font-semibold text-sm mb-1 flex items-center gap-2"><Star className="w-4 h-4 text-[hsl(var(--primary))]" /> Your tailored proposal is ready.</p>
            <p className="text-sm text-muted-foreground whitespace-pre-line">{status?.quote_description}</p>
          </div>

          {/* Custom fee structure */}
          <div className="border border-border bg-white p-5 space-y-3">
            <p className="text-xs uppercase tracking-widest text-muted-foreground" style={MONO}>Your Investment</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Monthly retainer (auto-renewing)</span>
                <span className="font-semibold">€{monthly.toLocaleString()} / month</span>
              </div>
              {setup > 0 && (
                <div className="flex justify-between border-t border-border pt-2">
                  <span className="text-muted-foreground">One-time setup fee</span>
                  <span className="font-semibold">€{setup.toLocaleString()}</span>
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground italic border-t border-border pt-3">
              Billed monthly via secure Stripe subscription. Cancel anytime — access continues until the end of the paid month.
            </p>
          </div>

          <LegalTerms accepted={termsAccepted} onAccept={setTermsAccepted} />

          {/* Due today */}
          <div className="border border-border bg-[hsl(var(--secondary))] p-4 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">First month retainer</span><span className="font-medium">€{monthly.toLocaleString()}</span></div>
            {setup > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Setup fee</span><span className="font-medium">€{setup.toLocaleString()}</span></div>}
            <div className="flex justify-between border-t border-border pt-2 font-semibold"><span>Total due today</span><span>€{dueToday.toLocaleString()}</span></div>
          </div>

          <button onClick={onActivate} disabled={activating || !termsAccepted}
            className="w-full flex items-center justify-center gap-2 py-3 bg-[hsl(var(--primary))] text-white text-sm font-medium disabled:opacity-50 transition-opacity"
            data-testid="dedicated-activate-btn">
            {activating ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting...</> : <>Accept & Activate — €{dueToday.toLocaleString()} <ArrowRight className="w-4 h-4" /></>}
          </button>
          {!termsAccepted && (
            <p className="text-xs text-center text-muted-foreground">Accept the service agreement above to proceed.</p>
          )}
        </div>
      )}

      {/* Active */}
      {isActive && (
        <div className="border border-[hsl(var(--primary)/0.4)] bg-[hsl(var(--primary)/0.06)] p-5" data-testid="dedicated-active-banner">
          <p className="font-semibold text-sm flex items-center gap-2"><Check className="w-4 h-4 text-[hsl(var(--primary))]" /> Service Active</p>
          <p className="text-sm text-muted-foreground mt-1">€{monthly.toLocaleString()}/month · auto-renewing</p>
          <p className="text-xs text-muted-foreground mt-2">Your dedicated scout will be in touch within 24 hours to begin your search. Use the messages below to stay in sync.</p>
        </div>
      )}

      {/* Pending (no quote yet) */}
      {status?.status === 'pending' && !quoteSent && (
        <div className="border border-border bg-[hsl(var(--secondary))] p-4 flex items-center gap-3">
          <Clock className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <div>
            <p className="text-sm font-medium">Enquiry under review</p>
            <p className="text-xs text-muted-foreground mt-0.5">We review every brief personally — typically within 24 hours. You'll be notified the moment your tailored quote is ready.</p>
          </div>
        </div>
      )}

      {/* Brief details */}
      <div className="border border-border p-5 space-y-2.5">
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3" style={MONO}>Your Brief</p>
        {[
          ['Role', status?.role_title],
          ['Level', status?.role_level],
          ['Salary Range', status?.expected_salary_min && status?.expected_salary_max ? `€${(status.expected_salary_min / 1000).toFixed(0)}k – €${(status.expected_salary_max / 1000).toFixed(0)}k` : 'Not specified'],
          ['Submitted', status?.created_at ? new Date(status.created_at).toLocaleDateString() : 'N/A'],
        ].map(([k, v]) => (
          <div key={k} className="flex justify-between text-sm">
            <span className="text-muted-foreground">{k}</span>
            <span className="font-medium">{v || 'N/A'}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────────
const DedicatedHiring = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [statusData, setStatusData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [activating, setActivating] = useState(false);
  const [sendingMsg, setSendingMsg] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadStatus();
    // Handle return from Stripe
    if (searchParams.get('activated') === '1') {
      const sessionId = searchParams.get('session_id');
      if (sessionId) pollPayment(sessionId, 'activation');
    }
  }, []);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${BACKEND}/api/dedicated/status`);
      const data = await res.json();
      if (data.success) {
        setStatusData(data);
        // Mark admin messages read on view + refresh sidebar badge
        if (data.unread > 0) {
          authFetch(`${BACKEND}/api/dedicated/messages/read`, { method: 'POST' })
            .then(() => window.dispatchEvent(new Event('dedicated-badge-refresh')))
            .catch(() => {});
        }
        // Load curated talent
        loadRecommendations();
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  const loadRecommendations = async () => {
    try {
      const res = await authFetch(`${BACKEND}/api/dedicated/recommendations`);
      const data = await res.json();
      if (data.success) setRecommendations(data.recommendations || []);
    } catch { /* silent */ }
  };

  const handleReact = async (recId, status) => {
    try {
      await authFetch(`${BACKEND}/api/dedicated/recommendations/${recId}/status`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      await loadRecommendations();
      window.dispatchEvent(new Event('dedicated-badge-refresh'));
    } catch { /* silent */ }
  };

  const handleSendMessage = async (text) => {
    setSendingMsg(true);
    try {
      const res = await authFetch(`${BACKEND}/api/dedicated/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text }),
      });
      const data = await res.json();
      if (data.success) await loadStatus();
      else setError(data.detail || 'Failed to send message');
    } catch { setError('Network error.'); }
    finally { setSendingMsg(false); }
  };

  const pollPayment = async (sessionId, type) => {
    try {
      const res = await authFetch(`${BACKEND}/api/dedicated/payment-status/${sessionId}`);
      const data = await res.json();
      if (data.success && data.payment_status === 'paid') {
        setSuccess(type === 'activation' ? 'Service activated! Our team will contact you within 24 hours.' : 'Placement confirmed!');
        await loadStatus();
      }
    } catch { /* silent */ }
  };

  const handleSubmitRequest = async (form) => {
    setSubmitting(true);
    setError('');
    try {
      const res = await authFetch(`${BACKEND}/api/dedicated/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.success) {
        setSuccess('Request submitted! Our team will review it within 24 hours.');
        await loadStatus();
      } else {
        setError(data.error || data.detail || 'Failed to submit request');
      }
    } catch { setError('Network error. Please try again.'); }
    finally { setSubmitting(false); }
  };

  const handleActivate = async () => {
    setActivating(true);
    try {
      const res = await authFetch(`${BACKEND}/api/dedicated/activate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ origin: window.location.origin }),
      });
      const data = await res.json();
      if (data.success && data.url) {
        window.location.href = data.url;
      } else {
        setError(data.detail || 'Failed to create checkout');
        setActivating(false);
      }
    } catch { setError('Network error.'); setActivating(false); }
  };

  const hasActiveRequest = statusData?.request && ['pending', 'approved', 'active', 'completed'].includes(statusData.request.status);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-8" data-testid="dedicated-hiring-page">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground border border-[hsl(var(--primary)/0.4)] bg-[hsl(var(--primary)/0.06)] px-3 py-1" style={MONO}>
            Exclusive Service
          </span>
        </div>
        <h1 className="text-3xl font-bold mb-2" style={SERIF}>Dedicated Hiring Service</h1>
        <p className="text-muted-foreground leading-relaxed max-w-2xl">
          Our team of fashion talent specialists manages your entire search — from brief to hire. Tell us the roles you need and we'll send a tailored monthly quote.
        </p>
      </div>

      {/* Key benefits */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { icon: <Star className="w-4 h-4" />, title: 'Dedicated scout', desc: 'A specialist assigned to your search' },
          { icon: <Shield className="w-4 h-4" />, title: 'Multiple roles', desc: 'Scout several positions in one engagement' },
          { icon: <Check className="w-4 h-4" />, title: 'Monthly, no lock-in', desc: 'Tailored monthly quote — cancel anytime' },
        ].map(b => (
          <div key={b.title} className="border border-border p-4">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-[hsl(var(--primary))]">{b.icon}</span>
              <span className="text-sm font-semibold">{b.title}</span>
            </div>
            <p className="text-xs text-muted-foreground">{b.desc}</p>
          </div>
        ))}
      </div>

      {success && (
        <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 px-4 py-3">
          <Check className="w-4 h-4 flex-shrink-0" />{success}
        </div>
      )}
      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 px-4 py-3">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />{error}
        </div>
      )}

      {/* Form or Status */}      <div className="border border-border bg-white">
        <div className="px-6 py-4 border-b border-border">
          <p className="text-xs uppercase tracking-widest text-muted-foreground" style={MONO}>
            {hasActiveRequest ? 'Service Status' : 'Request the Service'}
          </p>
        </div>
        <div className="p-6">
          {hasActiveRequest ? (
            <StatusPanel
              status={statusData.request}
              subscription={statusData.subscription}
              onActivate={handleActivate}
              activating={activating}
            />
          ) : (
            <RequestForm onSubmit={handleSubmitRequest} loading={submitting} />
          )}
        </div>
      </div>

      {/* Ticket-style message thread */}
      {hasActiveRequest && (
        <>
          <CuratedTalent recommendations={recommendations} onReact={handleReact} />
          <ClientThread messages={statusData.messages} onSend={handleSendMessage} sending={sendingMsg} />
        </>
      )}

      {/* What to expect — no pricing shown */}
      {!hasActiveRequest && (
        <div className="border border-border p-5 bg-[hsl(var(--secondary))] space-y-3">
          <p className="text-xs uppercase tracking-widest text-muted-foreground" style={MONO}>What happens next</p>
          <div className="space-y-2.5 text-sm">
            {[
              ['1. We review your brief', 'Our team personally reads every enquiry and assesses fit within 24 hours.'],
              ['2. We prepare your proposal', 'Accepted clients receive a tailored service proposal with full terms.'],
              ['3. You decide', 'No commitment until you review and accept the terms — at your own pace.'],
            ].map(([title, desc]) => (
              <div key={title} className="flex gap-3">
                <span className="font-semibold text-foreground flex-shrink-0 w-44">{title}</span>
                <span className="text-muted-foreground leading-relaxed">{desc}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DedicatedHiring;
